import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Navigate, Outlet, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { NotificationTab, SearchKind } from '@chapsi/contracts';
import { MaintenanceBanner } from './components/MaintenanceBanner.js';
import { Loading } from './components/States.js';
import { reportError } from './lib/sentry.js';
import { SignIn } from './screens/auth/SignIn.js';
import { Verify } from './screens/auth/Verify.js';
import { NotFound } from './screens/public/NotFound.js';
import { SHORTCUTS } from './api/chapi-shortcuts.js';

/**
 * The app shell and chat load as their own chunks, so client portal and share-link visitors download
 * neither. main.tsx starts fetching them straight away for everyone else, alongside this file.
 */
const loadShell = () => import('./components/AppShell.js');
const loadChannel = () => import('./screens/chat/ChannelLayout.js');
export const preloadApp = () => Promise.all([loadShell(), loadChannel()]);
const AppShell = lazyRouteComponent(loadShell, 'AppShell');
const ChannelLayout = lazyRouteComponent(loadChannel, 'ChannelLayout');
const ChannelChatTab = lazyRouteComponent(loadChannel, 'ChannelChatTab');
const HomeRedirect = lazyRouteComponent(() => import('./screens/chat/HomeRedirect.js'), 'HomeRedirect');

/**
 * `?task=<id>` opens the task drawer over any screen: drawer, not page (design principle 2).
 * Search values use `.catch(undefined)` so a mistyped or stale link falls back to the default view
 * instead of crashing the page.
 */
const AppSearch = z.object({ task: z.string().uuid().optional().catch(undefined) });

/** Every screen, signed in or out, sits under the maintenance banner (shown only while staff set one). */
const rootRoute = createRootRoute({
  component: () => (
    <div className="frame">
      <MaintenanceBanner />
      <div className="frame-body">
        <Outlet />
      </div>
    </div>
  ),
});

const signIn = createRoute({ getParentRoute: () => rootRoute, path: '/sign-in', component: SignIn });
const verify = createRoute({ getParentRoute: () => rootRoute, path: '/auth/verify', component: Verify });
/** Two-factor sign-in (docs/adr/0033): the second step, and the setup a workspace may require. */
const twoFactor = createRoute({ getParentRoute: () => rootRoute, path: '/auth/two-factor', component: lazyRouteComponent(() => import('./screens/auth/TwoFactor.js'), 'TwoFactor') });
const setUpTwoFactor = createRoute({ getParentRoute: () => rootRoute, path: '/auth/set-up-two-factor', component: lazyRouteComponent(() => import('./screens/auth/SetUpTwoFactor.js'), 'SetUpTwoFactor') });
const ssoDone = createRoute({ getParentRoute: () => rootRoute, path: '/auth/sso/done', component: lazyRouteComponent(() => import('./screens/auth/Sso.js'), 'SsoDone') });
// Back from Google or Microsoft with the session set (docs/adr/0081): the same landing as single sign-on.
const socialDone = createRoute({ getParentRoute: () => rootRoute, path: '/auth/social/done', component: lazyRouteComponent(() => import('./screens/auth/Sso.js'), 'SsoDone') });
const ssoRequired = createRoute({ getParentRoute: () => rootRoute, path: '/auth/sso-required', component: lazyRouteComponent(() => import('./screens/auth/Sso.js'), 'SsoRequired') });
// `for=me`: "Create your own workspace" from the switcher (docs/adr/0060).
const welcome = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: lazyRouteComponent(() => import('./screens/auth/Welcome.js'), 'Welcome'),
  validateSearch: z.object({ for: z.literal('me').optional().catch(undefined) }),
});
/** Deleting your own account (docs/adr/0040): the scheduled state, and the public how-to page (Google Play's link). */
const accountDeletion = createRoute({ getParentRoute: () => rootRoute, path: '/account-deletion', component: lazyRouteComponent(() => import('./screens/account/AccountPendingDeletion.js'), 'AccountPendingDeletion') });
const deleteAccountInfo = createRoute({ getParentRoute: () => rootRoute, path: '/delete-account', component: lazyRouteComponent(() => import('./screens/public/DeleteAccountInfo.js'), 'DeleteAccountInfo') });
const unsubscribe = createRoute({ getParentRoute: () => rootRoute, path: '/unsubscribe', component: lazyRouteComponent(() => import('./screens/public/Unsubscribe.js'), 'Unsubscribe') });
/**
 * A catch-up popped out into its own small window (docs/adr/0057): outside the app shell, so it is only the call.
 * `?pair=` names the window that opened it, for the handoff.
 */
const catchUpWindow = createRoute({
  getParentRoute: () => rootRoute,
  path: '/catch-up/$catchUpId',
  component: lazyRouteComponent(() => import('./catchup/PopOut.js'), 'PopOutPage'),
  validateSearch: z.object({ pair: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/).optional().catch(undefined) }),
});
/**
 * A My day item pinned to the desktop (docs/adr/0105): the desktop app's small always-on-top note window. Outside the app
 * shell, so it is only the note; the shell gives it its note, and it calls the API with the shared session.
 */
const noteWindow = createRoute({ getParentRoute: () => rootRoute, path: '/note/$noteId', component: lazyRouteComponent(() => import('./screens/notes/DesktopNote.js'), 'DesktopNotePage') });
const shared = createRoute({ getParentRoute: () => rootRoute, path: '/s/$token', component: lazyRouteComponent(() => import('./screens/public/SharedItem.js'), 'SharedItem') });
/** A Showcase invitation's free account page (docs/adr/0068, SC-16): outside the app, no session needed. */
const joinDesigns = createRoute({ getParentRoute: () => rootRoute, path: '/join/$token', component: lazyRouteComponent(() => import('./screens/showcase/JoinPage.js'), 'JoinPage') });

/**
 * The client portal (docs/adr/0027-client-helpdesk.md): pages for a client contact with no Chapsi
 * account, signed in by an emailed link. Outside the app route: no AppShell, no workspace hooks, no
 * redirect to the app's sign-in. Each page loads its own chunk, so a client never downloads the app.
 */
const Rating = z.enum(['great', 'ok', 'bad']);
const portalHome = createRoute({ getParentRoute: () => rootRoute, path: '/portal/$slug', component: lazyRouteComponent(() => import('./screens/portal/PortalHome.js'), 'PortalHome') });
const portalSignIn = createRoute({ getParentRoute: () => rootRoute, path: '/portal/$slug/sign-in', component: lazyRouteComponent(() => import('./screens/portal/PortalLinks.js'), 'PortalSignInLink') });
const portalTicketLink = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$slug/t',
  component: lazyRouteComponent(() => import('./screens/portal/PortalLinks.js'), 'PortalTicketLink'),
  validateSearch: z.object({ rate: Rating.optional().catch(undefined) }),
});
const portalTickets = createRoute({ getParentRoute: () => rootRoute, path: '/portal/$slug/tickets', component: lazyRouteComponent(() => import('./screens/portal/PortalHome.js'), 'PortalTickets') });
const portalTicket = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$slug/tickets/$ticketId',
  component: lazyRouteComponent(() => import('./screens/portal/PortalTicket.js'), 'PortalTicket'),
  validateSearch: z.object({ rated: Rating.optional().catch(undefined), rateFailed: z.boolean().optional().catch(undefined) }),
});
const portalNew = createRoute({ getParentRoute: () => rootRoute, path: '/portal/$slug/new', component: lazyRouteComponent(() => import('./screens/portal/PortalNew.js'), 'PortalNew') });
const portalWork = createRoute({ getParentRoute: () => rootRoute, path: '/portal/$slug/work', component: lazyRouteComponent(() => import('./screens/portal/PortalHome.js'), 'PortalWork') });
const portalHours = createRoute({
  getParentRoute: () => rootRoute,
  path: '/portal/$slug/hours',
  component: lazyRouteComponent(() => import('./screens/portal/PortalHours.js'), 'PortalHours'),
  /** `?month=YYYY-MM` opens a month's hours report (the link in the monthly email, docs/adr/0042). */
  validateSearch: z.object({ month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional().catch(undefined) }),
});

const app = createRoute({ getParentRoute: () => rootRoute, id: 'app', component: AppShell, validateSearch: AppSearch });
const home = createRoute({ getParentRoute: () => app, path: '/', component: HomeRedirect });
/**
 * v4 navigation (docs/adr/0082): Chat replaced the rail's Home and DMs, so their old addresses open Chat (the last
 * conversation) instead of a "not found".
 */
const oldHome = createRoute({ getParentRoute: () => app, path: '/home', beforeLoad: () => { throw redirect({ to: '/', replace: true }); } });
const oldDms = createRoute({ getParentRoute: () => app, path: '/dms', beforeLoad: () => { throw redirect({ to: '/', replace: true }); } });
/** `?msg=<id>` scrolls to a message and highlights it (links from the Inbox, search, notifications and reply quotes). */
/** `?tab=`/`?view=` are older project-channel links (docs/adr/0078): the layout moves them to the project page (docs/adr/0091). */
const channel = createRoute({
  getParentRoute: () => app,
  path: '/c/$channelId',
  component: ChannelLayout,
  validateSearch: z.object({
    msg: z.string().uuid().optional().catch(undefined),
    tab: z.enum(['chat', 'tasks', 'support', 'files', 'hours', 'estimates', 'designs', 'updates', 'repository']).optional().catch(undefined),
    view: z.enum(['board', 'list', 'calendar']).optional().catch(undefined),
  }),
});
const channelChat = createRoute({ getParentRoute: () => channel, path: '/', component: ChannelChatTab });
/** The Project filter in the URL: a project id, or "none:<client id>" / "none:" for tasks without a project. */
const projectFilter = z.string().regex(/^([0-9a-f-]{36}|none:([0-9a-f-]{36})?)$/i).optional().catch(undefined);
const channelTasks = createRoute({
  getParentRoute: () => channel,
  path: '/tasks',
  component: lazyRouteComponent(() => import('./screens/work/ChannelTasks.js'), 'ChannelTasks'),
  validateSearch: z.object({ view: z.enum(['board', 'list', 'calendar']).optional().catch(undefined), project: projectFilter }),
});
const channelSupport = createRoute({ getParentRoute: () => channel, path: '/support', component: lazyRouteComponent(() => import('./screens/work/ChannelSupport.js'), 'ChannelSupport') });
/** A project channel's Files tab (docs/adr/0091): the project's files, the same list as the project page's. */
const channelFiles = createRoute({ getParentRoute: () => channel, path: '/files', component: lazyRouteComponent(() => import('./screens/projects/ProjectFiles.js'), 'ChannelFilesTab') });
const channelHours = createRoute({ getParentRoute: () => channel, path: '/hours', component: lazyRouteComponent(() => import('./screens/work/ChannelHours.js'), 'ChannelHours') });
/** Estimates (docs/adr/0090): the client's quotes, as a tab next to Tasks and Hours. Only while Invoicing is on. */
const channelEstimates = createRoute({ getParentRoute: () => channel, path: '/estimates', component: lazyRouteComponent(() => import('./screens/estimates/ChannelEstimates.js'), 'ChannelEstimates') });
/** Repositories (docs/adr/0052-repositories.md): the projects' pushes and pull requests. Only while Repositories is on. */
/** Showcase (docs/adr/0065): the channel's Designs tab, and the full-screen editor over the app. */
const channelDesigns = createRoute({ getParentRoute: () => channel, path: '/designs', component: lazyRouteComponent(() => import('./screens/showcase/DesignsTab.js'), 'DesignsTab') });
const EditorSearch = z.object({
  from: z.string().uuid().optional().catch(undefined),
  mode: z.enum(['flow', 'preview', 'feedback', 'compare']).optional().catch(undefined),
  /** An older version to open read-only (docs/adr/0069). */
  version: z.coerce.number().int().min(1).optional().catch(undefined),
  /** Open the "Upload version N" dialog (docs/adr/0069, SC-21). */
  upload: z.literal(true).optional().catch(undefined),
  screen: z.string().uuid().optional().catch(undefined),
  /** Feedback (docs/adr/0068): the comment to open, from a task's link or a notification. */
  comment: z.string().uuid().optional().catch(undefined),
});
const designEditor = createRoute({ getParentRoute: () => app, path: '/designs/$setId', component: lazyRouteComponent(() => import('./screens/showcase/Editor.js'), 'Editor'), validateSearch: EditorSearch });
/** The client's review page (docs/adr/0068, SC-07); the team can open it too, to see what clients see. */
const designReview = createRoute({ getParentRoute: () => app, path: '/review/$setId', component: lazyRouteComponent(() => import('./screens/showcase/ReviewPage.js'), 'ReviewPage') });
const channelRepository = createRoute({ getParentRoute: () => channel, path: '/repository', component: lazyRouteComponent(() => import('./screens/repos/ChannelRepository.js'), 'ChannelRepository') });
/** Client progress updates (docs/adr/0063): Chapi's drafts, reviewed and sent by a person. Chapi and Tasks on, team only. */
const channelUpdates = createRoute({
  getParentRoute: () => channel,
  path: '/updates',
  component: lazyRouteComponent(() => import('./screens/chapi/ClientUpdates.js'), 'ChannelClientUpdates'),
  validateSearch: z.object({ project: z.string().uuid().optional().catch(undefined) }),
});
/** A channel's side panel popped out into its own window (docs/adr/0077): inside the app shell, without the sidebar. */
const panelWindow = createRoute({ getParentRoute: () => app, path: '/panel/$channelId', component: lazyRouteComponent(() => import('./screens/chat/PanelWindow.js'), 'PanelWindow') });
/** A channel popped out into its own window (docs/adr/0094): the channel and its header, in the app shell without the sidebar. */
const popChannel = createRoute({ getParentRoute: () => app, path: '/pop/c/$channelId', component: lazyRouteComponent(() => import('./screens/chat/PopChannel.js'), 'PopChannel') });
const myWork = createRoute({ getParentRoute: () => app, path: '/my-work', component: lazyRouteComponent(() => import('./screens/mywork/MyWork.js'), 'MyWork') });
/** My day (docs/adr/0029-my-day.md): the person's own short list for today. */
const myDay = createRoute({
  getParentRoute: () => app,
  path: '/my-day',
  component: lazyRouteComponent(() => import('./screens/myday/MyDay.js'), 'MyDay'),
  /** `?plan=true` opens Plan my day (from My work's Chapi card). */
  // `item`: open with that item highlighted (a My day nudge, docs/adr/0107).
  validateSearch: z.object({ plan: z.boolean().optional().catch(undefined), item: z.string().uuid().optional().catch(undefined) }),
});
/**
 * The Notifications centre (SC-20, docs/adr/0048): tabs by what a notification is about, Unread only, and Reminders.
 * `?n=<id>` keeps the notification open in the preview, so a reload or a shared screen shows the same one.
 */
const notificationsPage = createRoute({
  getParentRoute: () => app,
  path: '/notifications',
  component: lazyRouteComponent(() => import('./screens/notifications/NotificationsCentre.js'), 'NotificationsCentre'),
  validateSearch: z.object({
    tab: z.enum([...NotificationTab.options, 'reminders']).optional().catch(undefined),
    unread: z.boolean().optional().catch(undefined),
    sent: z.boolean().optional().catch(undefined),
    n: z.string().uuid().optional().catch(undefined),
  }),
});
/** The old Inbox address: bookmarks, emails and older apps still land on the centre (Unread became a toggle). */
const inbox = createRoute({
  getParentRoute: () => app,
  path: '/inbox',
  validateSearch: z.object({ tab: z.enum(['all', 'unread', 'reminders']).optional().catch(undefined), sent: z.boolean().optional().catch(undefined), task: z.string().uuid().optional().catch(undefined) }),
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/notifications',
      search: { ...(search.tab === 'reminders' ? { tab: 'reminders' as const } : {}), ...(search.tab === 'unread' ? { unread: true } : {}), ...(search.sent ? { sent: true } : {}), ...(search.task ? { task: search.task } : {}) },
      replace: true,
    });
  },
});
/** Timesheets and monthly reports (docs/adr/0042-timesheets-and-retainers.md). Only while Time is on. */
const time = createRoute({
  getParentRoute: () => app,
  path: '/time',
  component: lazyRouteComponent(() => import('./screens/time/TimePage.js'), 'TimePage'),
  validateSearch: z.object({
    tab: z.enum(['sheet', 'report']).optional().catch(undefined),
    week: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined),
    person: z.string().uuid().optional().catch(undefined),
    project: z.string().uuid().optional().catch(undefined),
    client: z.string().uuid().optional().catch(undefined),
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/).optional().catch(undefined),
  }),
});
/** Invoices to the workspace's clients (docs/adr/0054-invoicing-and-xero.md). Only while Invoicing is on. */
const invoices = createRoute({
  getParentRoute: () => app,
  path: '/invoices',
  component: lazyRouteComponent(() => import('./screens/invoicing/InvoicesPage.js'), 'InvoicesPage'),
  validateSearch: z.object({ tab: z.enum(['list', 'new', 'pending', 'assign', 'recorded', 'rates', 'xero']).optional().catch(undefined) }),
});
/** Client estimates (docs/adr/0088, 0090): the list and one estimate. Only while Invoicing is on. */
const estimates = createRoute({ getParentRoute: () => app, path: '/estimates', component: lazyRouteComponent(() => import('./screens/estimates/EstimatesPage.js'), 'EstimatesPage') });
const estimate = createRoute({ getParentRoute: () => app, path: '/estimates/$estimateId', component: lazyRouteComponent(() => import('./screens/estimates/EstimateDetail.js'), 'EstimateDetail') });
const invoice = createRoute({ getParentRoute: () => app, path: '/invoices/$invoiceId', component: lazyRouteComponent(() => import('./screens/invoicing/InvoiceDetail.js'), 'InvoiceDetail') });
/** Xero sends the browser back here after sign-in; the page hands the code and state to the API. */
const xeroCallback = createRoute({
  getParentRoute: () => app,
  path: '/invoicing/xero/callback',
  component: lazyRouteComponent(() => import('./screens/invoicing/XeroCallback.js'), 'XeroCallback'),
  validateSearch: z.object({ code: z.string().max(2000).optional().catch(undefined), state: z.string().max(200).optional().catch(undefined), error: z.string().max(200).optional().catch(undefined) }),
});
/** The printable invoice, outside the app shell so only the invoice prints. */
const invoicePrint = createRoute({ getParentRoute: () => rootRoute, path: '/invoices/$invoiceId/print', component: lazyRouteComponent(() => import('./screens/invoicing/InvoicePrint.js'), 'InvoicePrint') });
/** Reports (docs/adr/0053-reports.md). Only while Reports is on; never for guests. */
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().catch(undefined);
const reports = createRoute({
  getParentRoute: () => app,
  path: '/reports',
  component: lazyRouteComponent(() => import('./screens/reports/ReportsPage.js'), 'ReportsPage'),
  validateSearch: z.object({
    tab: z.enum(['time', 'retainers', 'throughput', 'support', 'workload']).optional().catch(undefined),
    from: isoDate,
    to: isoDate,
    client: z.string().uuid().optional().catch(undefined),
    project: z.string().uuid().optional().catch(undefined),
    person: z.string().uuid().optional().catch(undefined),
    interval: z.enum(['week', 'month']).optional().catch(undefined),
  }),
});
const projects = createRoute({ getParentRoute: () => app, path: '/projects', component: lazyRouteComponent(() => import('./screens/projects/Projects.js'), 'Projects') });
/**
 * A project's page (docs/adr/0091): `?tab=` is the section (Overview without one). `chat` and `tasks` are older addresses
 * (docs/adr/0078): the page sends `chat` (with `msg`) to the project's channel and `tasks` to List, Board or Calendar by
 * `?view=`. `?project=` is the Client updates and Repository sections' own filter.
 */
const projectPage = createRoute({
  getParentRoute: () => app,
  path: '/projects/$projectId',
  component: lazyRouteComponent(() => import('./screens/projects/ProjectPage.js'), 'ProjectPage'),
  validateSearch: z.object({
    tab: z.enum(['overview', 'list', 'board', 'files', 'hours', 'estimates', 'support', 'designs', 'updates', 'repository', 'calendar', 'rules', 'chat', 'tasks']).optional().catch(undefined),
    /** Project rules (docs/adr/0109): the builder (`new` or a rule id), a template to start from, or a rule's history. */
    rule: z.string().max(40).optional().catch(undefined),
    template: z.string().max(20).optional().catch(undefined),
    history: z.string().uuid().optional().catch(undefined),
    view: z.enum(['board', 'list', 'calendar']).optional().catch(undefined),
    msg: z.string().uuid().optional().catch(undefined),
    project: z.string().uuid().optional().catch(undefined),
  }),
});
/** The Clients section (docs/adr/0078): every client the person may see, and one client's page. Team only. */
/** `?view=` a saved view from the panel (UI-12): clients that need attention, or the retainers. */
const clientsPage = createRoute({
  getParentRoute: () => app,
  path: '/clients',
  component: lazyRouteComponent(() => import('./screens/clients/ClientsPage.js'), 'ClientsPage'),
  validateSearch: z.object({ view: z.enum(['attention', 'retainers']).optional().catch(undefined) }),
});
/** `?tab=` the client page's section (UI-13, docs/adr/0084); Overview without one. */
const clientPage = createRoute({
  getParentRoute: () => app,
  path: '/clients/$clientId',
  component: lazyRouteComponent(() => import('./screens/clients/ClientPage.js'), 'ClientPage'),
  validateSearch: z.object({ tab: z.enum(['overview', 'projects', 'support', 'hours', 'invoices', 'designs']).optional().catch(undefined) }),
});
const allTasks = createRoute({
  getParentRoute: () => app,
  path: '/tasks',
  component: lazyRouteComponent(() => import('./screens/work/AllTasks.js'), 'AllTasks'),
  validateSearch: z.object({ view: z.enum(['board', 'list']).optional().catch(undefined), project: projectFilter }),
});
/** `/search?q=&kind=&from=&in=&after=&before=&when=`: the full results page behind ⌘K (docs/adr/0012-search.md). */
const search = createRoute({
  getParentRoute: () => app,
  path: '/search',
  component: lazyRouteComponent(() => import('./screens/search/SearchPage.js'), 'SearchPage'),
  validateSearch: z.object({
    q: z.string().transform((s) => s.slice(0, 200)).optional().catch(undefined),
    kind: z.enum(SearchKind.options).optional().catch(undefined),
    // Filter chips (docs/adr/0036-search-by-meaning.md): a person, a channel, and a date range with its words.
    from: z.string().uuid().optional().catch(undefined),
    in: z.string().uuid().optional().catch(undefined),
    after: z.string().datetime({ offset: true }).optional().catch(undefined),
    before: z.string().datetime({ offset: true }).optional().catch(undefined),
    when: z.string().transform((s) => s.slice(0, 40)).optional().catch(undefined),
  }),
});
/**
 * Chapi (docs/adr/0021-chapi.md). `/chapi?ask=<shortcut>&id=<item>` (or `?q=<question>`) starts a new
 * conversation and asks at once: the entry points in chat use it. The page clears the params after asking.
 */
const ChapiSearch = z.object({
  ask: z.enum(SHORTCUTS).optional().catch(undefined),
  id: z.string().uuid().optional().catch(undefined),
  q: z.string().transform((s) => s.slice(0, 4000)).optional().catch(undefined),
});
const chapi = createRoute({ getParentRoute: () => app, path: '/chapi', component: lazyRouteComponent(() => import('./screens/chapi/ChapiPage.js'), 'ChapiPage'), validateSearch: ChapiSearch });
const chapiConversation = createRoute({ getParentRoute: () => app, path: '/chapi/$conversationId', component: lazyRouteComponent(() => import('./screens/chapi/ChapiPage.js'), 'ChapiPage') });
const support = createRoute({ getParentRoute: () => app, path: '/support', component: lazyRouteComponent(() => import('./screens/work/SupportQueue.js'), 'SupportQueue') });
const settings = createRoute({ getParentRoute: () => app, path: '/settings/$section', component: lazyRouteComponent(() => import('./screens/settings/WorkspaceSettings.js'), 'WorkspaceSettings') });
const settingsIndex = createRoute({ getParentRoute: () => app, path: '/settings', component: () => <Navigate to="/settings/$section" params={{ section: 'general' }} /> });
const account = createRoute({ getParentRoute: () => app, path: '/account/$section', component: lazyRouteComponent(() => import('./screens/settings/AccountSettings.js'), 'AccountSettings') });

const routeTree = rootRoute.addChildren([
  unsubscribe,
  signIn,
  verify,
  twoFactor,
  setUpTwoFactor,
  welcome,
  ssoDone,
  socialDone,
  ssoRequired,
  accountDeletion,
  deleteAccountInfo,
  shared,
  joinDesigns,
  catchUpWindow,
  noteWindow,
  portalHome,
  portalSignIn,
  portalTicketLink,
  portalTickets,
  portalTicket,
  portalNew,
  portalWork,
  portalHours,
  invoicePrint,
  app.addChildren([
    home,
    oldHome,
    oldDms,
    channel.addChildren([channelChat, channelTasks, channelSupport, channelFiles, channelHours, channelEstimates, channelRepository, channelUpdates, channelDesigns]),
    designEditor,
    designReview,
    panelWindow,
    popChannel,
    myWork,
    myDay,
    notificationsPage,
    inbox,
    projects,
    projectPage,
    clientsPage,
    clientPage,
    time,
    invoices,
    invoice,
    estimates,
    estimate,
    xeroCallback,
    reports,
    allTasks,
    search,
    chapi,
    chapiConversation,
    support,
    settingsIndex,
    settings,
    account,
  ]),
]);

/**
 * Every screen loads its own chunk, preloaded when a link is hovered or focused ('intent'), so moving
 * there still feels instant. The shell and chat are fetched at start-up for app visitors (main.tsx).
 */
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: () => <Loading rows={6} />,
  defaultNotFoundComponent: NotFound,
  defaultOnCatch: (err) => reportError(err),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
